<template>
  <div>
    <button @click.prevent="openNewOperation" class="btn btn-primary mb-4">
      <i class="bi bi-plus-lg me-1 mb-1"></i>Add event
    </button>
    <NewOperation
      name="new-operation-modal"
      v-model="isNewOperationModalOpen"
      @cancel="closeNewOperationModal"
      @confirm="onOperationCreated"
    >
      <template v-slot:title>New event</template>
    </NewOperation>
    <!--begin::Table-->
    <vue-good-table
      mode="remote"
      :total-rows="totalRecords"
      :is-loading="isTableLoading"
      :pagination-options="{ enabled: true }"
      :sort-options="{ enabled: false, initialSortBy: serverParams.sort }"
      :rows="rows"
      :columns="columns"
      :fixed-header="true"
      @page-change="onPageChange"
      @sort-change="onSortChange"
      @column-filter="onColumnFilter"
      @per-page-change="onPerPageChange"
      @row-click="toOperation"
      styleClass="custom-table clickable"
    >
      <template #loading-content class="text-center">Loading...</template>
      <template #emptystate class="text-center">No entry found</template>

      <template #column-filter="{ column, updateFilters }">
        <template
          v-if="column.filterOptions && column.filterOptions.customFilter"
        >
          <v-select
            @option:selected="
              (e) =>
                e.id ? updateFilters(column, e.id) : updateFilters(column, null)
            "
            @search="onSearch"
            :reduce="(e) => e.id"
            :filterable="false"
            :options="patientsOptions"
            placeholder="Name"
            required
            label="id"
          >
            <template #no-options>
              Search by first/lastname or email address...
            </template>
            <template #option="option">
              <div class="d-center">
                <span v-if="option.firstname"
                  >{{ option.firstname }} {{ option.lastname }}</span
                >
                <span v-else>{{ option.id }}</span>
              </div>
            </template>
            <template #selected-option="option">
              <div class="selected d-center">
                <span v-if="option.firstname"
                  >{{ option.firstname }} {{ option.lastname }}</span
                >
                <span v-else>{{ option.id }}</span>
              </div>
            </template>
          </v-select>
        </template>
      </template>

      <template #table-row="props">
        <span v-if="props.column.field == 'type'">
          <span
            class="badge badge-square type py-5 px-3"
            :class="`badge-${getTypeBadgeColorClass(props.row.type)}`"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
            :title="props.row.type"
            ><i
              :class="`${getTypeIcon(props.row.type)} text-${getTypeColorClass(
                props.row.type
              )} fs-3`"
            ></i
          ></span>
        </span>
        <span v-else-if="props.column.field == 'patient'">
          <span class="patient">{{
            props.row.patient
              ? `${props.row.patient.firstname} ${props.row.patient.lastname}`
              : "N/C"
          }}</span>
        </span>
        <span v-else-if="props.column.field == 'downPayment'">
          <span
            class="badge down-payment"
            :class="getDownPaymentClass(props.row.downPayment)"
            >{{ props.row.downPayment ? "Paid" : "Waiting for payment" }}</span
          >
        </span>
        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template #pagination-bottom="props">
        <div
          class="d-flex justify-content-between flex-wrap align-items-center"
        >
          <div class="d-flex align-items-center mb-0 mt-1">
            <select
              class="form-control form-control-sm me-3"
              :value="serverParams.perPage"
              @change="
                (e) => props.perPageChanged({ currentPerPage: e.target.value })
              "
            >
              <option
                v-for="(option, idx) in ['5', '10', '25', '50', '100']"
                :key="`select-perpage-${idx}`"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
            <span class="text-nowrap"> operations per page </span>
          </div>
          <div>
            <ul class="pagination">
              <li
                class="page-item previous"
                :class="{ disabled: serverParams.page === 1 }"
              >
                <a
                  @click.prevent="
                    props.pageChanged({ currentPage: serverParams.page - 1 })
                  "
                  href="#"
                  class="page-link"
                  ><i class="previous"></i
                ></a>
              </li>
              <div
                v-for="idx in Math.ceil(props.total / serverParams.perPage)"
                class="d-flex flex-row align-items-center"
                :key="idx"
              >
                <li
                  class="page-item"
                  :class="{ active: serverParams.page === idx }"
                >
                  <a
                    @click.prevent="props.pageChanged({ currentPage: idx })"
                    href="#"
                    class="page-link"
                    v-if="
                      idx <= 3 ||
                      (idx >= serverParams.page - 1 &&
                        idx <= serverParams.page + 1) ||
                        (idx >= Math.ceil(props.total / serverParams.perPage) - 2)
                    "
                    >{{ idx }}</a
                  >
                </li>
                <p class="mb-0 mt-1 mx-3" v-if="(idx === serverParams.page - 2 && idx > 3) || (idx === serverParams.page + 2 && (idx < Math.ceil(props.total / serverParams.perPage) - 2))">...</p>
              </div>
              <li
                class="page-item next"
                :class="{
                  disabled:
                    serverParams.page ===
                    Math.ceil(props.total / serverParams.perPage),
                }"
              >
                <a
                  @click.prevent="
                    props.pageChanged({ currentPage: serverParams.page + 1 })
                  "
                  href="#"
                  class="page-link"
                  ><i class="next"></i
                ></a>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </vue-good-table>
    <!--end::Table-->
  </div>
</template>

<script>
import ApiService from "@/core/services/ApiService";
import NewOperation from "@/components/modals/operations/NewOperation.vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import EventType from "@/core/constants/EventType.ts";

import _ from "lodash";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "OperationsManager",
  components: {
    NewOperation,
    vSelect,
  },
  data() {
    return {
      isNewOperationModalOpen: false,
      isTableLoading: false,
      totalRecords: 0,
      columns: [
        {
          label: "Type",
          field: "type",
          sortable: true,
        },
        {
          label: "Date",
          field: "start",
          sortable: true,
          formatFn: (dt) =>
            new Date(dt).toLocaleDateString([], {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "numeric",
            }),
        },
        {
          label: "Duration",
          field: "duration",
          formatFn: this.getDuration,
        },
        {
          label: "Patient",
          field: "patient",
          filterOptions: {
            enabled: true,
            customFilter: true,
          },
        },
        {
          label: "Doctor",
          field: "doctor",
          formatFn: (doctor) => (doctor ? `Dr. ${doctor.lastname}` : "N/C"),
          filterOptions: {
            enabled: true,
            placeholder: "All",
            filter: true,
            filterDropdownItems: [], // Async loaded
          },
        },
        {
          label: "Down payment",
          field: "downPayment",
          formatFn: (e) => (e ? "Paid" : "Waiting for payment"),
          filterOptions: {
            enabled: true,
            placeholder: "All",
            filter: true,
            filterDropdownItems: [
              { value: true, text: "Paid" },
              { value: false, text: "Waiting for payment" },
            ], // Async loaded
          },
        },
        {
          label: "Products",
          field: "products",
          formatFn: (products) => products.map((e) => e.name).join(", "),
        },
      ],
      rows: [],
      serverParams: {
        columnFilters: {},
        sort: {
          field: "start",
          type: "desc",
        },
        page: 1,
        perPage: 25,
      },

      // Filter options
      patientsOptions: [],
    };
  },
  mounted() {
    this.fetchItemsFromApi();
    setCurrentPageBreadcrumbs("Events", []);

    ApiService.query("users", { params: { isDoctor: true } }).then(
      ({ data: { results } }) => {
        this.columns.find(
          (e) => e.field === "doctor"
        ).filterOptions.filterDropdownItems = results.map((doctor) => ({
          value: doctor.id,
          text: `${doctor.firstname} ${doctor.lastname}`,
        }));
      }
    );
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = { ...this.serverParams, ...newProps };
    },
    onPageChange(params) {
      console.log("onPageChange", params);
      this.updateParams({ page: params.currentPage });
      this.fetchItemsFromApi();
    },
    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.onPageChange({ currentPage: 1 });
    },
    onSortChange([params]) {
      this.updateParams({
        sort: {
          field: params.field,
          type: params.type,
        },
      });
      this.onPageChange({ currentPage: 1 });
    },
    onColumnFilter(params) {
      this.updateParams(params);
      this.onPageChange({ currentPage: 1 });
    },
    /**
     * Fetch models from API and display them in the table
     */
    fetchItemsFromApi() {
      this.isTableLoading = true;

      const tableSearch = {};
      Object.keys(this.serverParams.columnFilters).forEach((e) => {
        if (!this.serverParams.columnFilters[e]) return;
        if (["patient", "doctor", "duration", "downPayment"].includes(e)) {
          tableSearch[e] = this.serverParams.columnFilters[e];
        } else {
          tableSearch[`${e}_contains`] = this.serverParams.columnFilters[e];
        }
      });

      const getApiParams = {
        ...tableSearch,
        limit: this.serverParams.perPage,
        page: this.serverParams.page,
        sortBy: `${this.serverParams.sort.field}:${this.serverParams.sort.type}`,
        populate: "doctor patient products",
      };

      ApiService.query("/operations", { params: getApiParams })
        .then((res) => {
          this.rows = res.data.results;
          this.totalRecords = res.data.totalResults;
          this.isTableLoading = false;
        })
        .catch((err) => {
          this.isTableLoading = false;
        });
    },
    openNewOperation(customOperation) {
      this.$vfm.show("new-operation-modal", customOperation || this.operation);
    },
    closeNewOperationModal() {
      this.isNewOperationModalOpen = false;
    },
    onOperationCreated(createdOperation) {
      this.$vfm.hide("new-operation-modal");
      this.$router.push({
        name: "operation",
        params: { id: createdOperation.id },
      });
    },
    toOperation(params) {
      this.$router.push(`/operations/${params.row.id}`);
    },
    getDownPaymentClass(downPayment) {
      return {
        "badge-light-success": downPayment,
        "text-success": downPayment,
        "badge-light-warning": !downPayment,
        "text-warning": !downPayment,
      };
    },
    getTypeBadgeColorClass(type) {
      return EventType[type].badgeColorClass;
    },
    getTypeColorClass(type) {
      return EventType[type].colorClass;
    },
    getTypeIcon(type) {
      return EventType[type].icon;
    },
    getDuration(duration) {
      let minutes = duration.minutes;
      if (minutes < 10) {
        minutes = "0" + minutes;
      }

      return duration.hours ? `${duration.hours}h${minutes}` : `${minutes}min`;
    },

    // Custom filter
    onSearch(search, loading) {
      if (search.length) {
        loading(true);
        this.search(loading, search, this);
      }
    },
    search: _.debounce((loading, search, vm) => {
      ApiService.query("/patients", {
        params: {
          firstname_contains: search,
          lastname_contains: search,
          email_contains: search,
          _or: 1,
        },
      })
        .then((res) => {
          vm.patientsOptions = ["All", ...res.data.results];
          loading(false);
        })
        .catch((err) => {
          vm.patientsOptions = [];
          loading(false);
        });
    }, 350),
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  z-index: 10;
}

.operations-table {
  background-color: #fff;
}
.patient {
  // font-weight: 700;
}

.down-payment {
  font-size: 13px;
}
</style>

<style lang="scss">
.v-select .vs__dropdown-toggle {
  padding: 2px;
  border-radius: 4px;
  box-shadow: none;
  min-width: 144px;
}

.vs__open-indicator {
  transform: scale(0.6) !important;
}
</style>